import type {
    AccentId,
    ChatPreferences,
    FontSizeId,
    ImageAspect,
    ImageQuality,
    ImageStyleId,
    Settings,
    SlideThemeId,
    SlideTone,
    StudioMode,
    ThemePreference,
    VideoAspect,
    VideoResolution,
    VideoStyleId
} from '@/types'

type Option<T extends string> = {
    id: T
    label: string
}

export const VIDEO_MIN_SECONDS = 1
export const VIDEO_MAX_SECONDS = 120
export const SLIDES_MIN = 4
export const SLIDES_MAX = 20

export const ATTACHMENT_LIMITS = {
    images: 4,
    pdfs: 2,
    pdfBytes: 40 * 1024 * 1024,
    pdfPages: 80,
    pdfChars: 120000,
    imageSide: 1600
}

export const MODES: Array<Option<StudioMode> & { hint: string, placeholder: string }> = [
    {
        id: 'chat',
        label: 'دردشة',
        hint: 'اسأل، أرفق صورة أو ملف PDF، أو اطلب صورة وفيديو وعرضا داخل المحادثة',
        placeholder: 'اكتب رسالتك... ويمكنك أن تطلب صورة أو فيديو أو عرضا تقديميا'
    },
    {
        id: 'image',
        label: 'صورة',
        hint: 'صف الصورة التي تتخيلها، أو أرفق صورة لتعديلها',
        placeholder: 'صف الصورة بالتفصيل: الموضوع، المكان، الإضاءة، الألوان...'
    },
    {
        id: 'video',
        label: 'فيديو',
        hint: 'صف المشهد واختر المدة من ثانية حتى 120 ثانية',
        placeholder: 'صف الفيديو: ماذا يحدث، أين، حركة الكاميرا، الأجواء...'
    },
    {
        id: 'slides',
        label: 'عرض تقديمي',
        hint: 'اكتب موضوع العرض وما تريد تغطيته',
        placeholder: 'موضوع العرض التقديمي والجمهور والنقاط المهمة...'
    }
]

export const IMAGE_STYLES: Array<Option<ImageStyleId> & { prompt: string }> = [
    { id: 'auto', label: 'تلقائي', prompt: '' },
    { id: 'photo', label: 'واقعي', prompt: 'photorealistic photograph, shot on a full-frame camera with an 85mm lens, natural soft light, ultra-detailed, sharp focus, professional color grading' },
    { id: 'cinematic', label: 'سينمائي', prompt: 'cinematic film still, dramatic lighting, anamorphic lens, shallow depth of field, rich color grading, subtle film grain' },
    { id: 'digital-art', label: 'فن رقمي', prompt: 'high-end digital painting, concept art, intricate details, vibrant harmonious colors, masterful composition' },
    { id: '3d', label: 'ثلاثي الأبعاد', prompt: 'stylized 3D render, soft global illumination, studio lighting, smooth materials, high detail' },
    { id: 'anime', label: 'أنمي', prompt: 'anime illustration, clean line art, cel shading, vibrant palette, detailed background, studio quality' },
    { id: 'watercolor', label: 'ألوان مائية', prompt: 'watercolor painting on textured paper, soft washes, delicate details, artistic and elegant' },
    { id: 'product', label: 'تصوير منتجات', prompt: 'professional product photography, seamless studio background, softbox lighting, crisp reflections, premium advertising quality' },
    { id: 'minimal', label: 'بسيط', prompt: 'minimalist design, clean composition, generous negative space, flat refined colors, modern aesthetic' },
    { id: 'logo', label: 'شعار', prompt: 'professional logo design, flat vector style, simple bold shapes, balanced and memorable, centered on a plain background, brand identity quality' }
]

export const IMAGE_ASPECTS: Array<Option<ImageAspect>> = [
    { id: '1:1', label: 'مربع 1:1' },
    { id: '16:9', label: 'عريض 16:9' },
    { id: '9:16', label: 'طولي 9:16' },
    { id: '4:3', label: 'أفقي 4:3' },
    { id: '3:4', label: 'عمودي 3:4' }
]

export const IMAGE_QUALITIES: Array<Option<ImageQuality> & { description: string }> = [
    { id: 'standard', label: 'عادية', description: 'أسرع، بدقة 1K' },
    { id: 'high', label: 'عالية', description: 'تفاصيل أدق، بدقة 2K' },
    { id: 'ultra', label: 'فائقة', description: 'أعلى جودة، بدقة 4K' }
]

export const VIDEO_STYLES: Array<Option<VideoStyleId> & { prompt: string }> = [
    { id: 'auto', label: 'تلقائي', prompt: '' },
    { id: 'cinematic', label: 'سينمائي', prompt: 'cinematic look, dramatic lighting, smooth camera movement, film color grading' },
    { id: 'documentary', label: 'وثائقي', prompt: 'documentary style, natural light, realistic handheld camera, authentic details' },
    { id: 'commercial', label: 'إعلاني', prompt: 'high-end commercial advertisement, polished lighting, dynamic camera moves, premium look' },
    { id: 'anime', label: 'أنمي', prompt: 'anime style animation, vibrant colors, expressive motion' },
    { id: '3d', label: 'رسوم ثلاثية الأبعاد', prompt: '3D animated film style, soft lighting, expressive characters, high detail' }
]

export const VIDEO_ASPECTS: Array<Option<VideoAspect>> = [
    { id: '16:9', label: 'عريض 16:9' },
    { id: '9:16', label: 'طولي 9:16' },
    { id: '1:1', label: 'مربع 1:1' },
    { id: '4:3', label: 'أفقي 4:3' },
    { id: '3:4', label: 'عمودي 3:4' }
]

export const VIDEO_RESOLUTIONS: Array<Option<VideoResolution>> = [
    { id: '480p', label: '480p' },
    { id: '720p', label: '720p' },
    { id: '1080p', label: '1080p' }
]

export const SLIDE_TONES: Array<Option<SlideTone>> = [
    { id: 'professional', label: 'احترافي' },
    { id: 'educational', label: 'تعليمي' },
    { id: 'marketing', label: 'تسويقي' },
    { id: 'inspiring', label: 'ملهم' }
]

export type SlideTheme = {
    id: SlideThemeId
    label: string
    bg: string
    surface: string
    fg: string
    muted: string
    accent: string
    accent2: string
}

export const SLIDE_THEMES: Array<SlideTheme> = [
    { id: 'royal', label: 'ملكي', bg: '#0B1530', surface: '#14214A', fg: '#F7F2E7', muted: '#AEB7D3', accent: '#D9AC4B', accent2: '#F2D58C' },
    { id: 'modern', label: 'حديث', bg: '#FFFFFF', surface: '#F2F3F8', fg: '#111827', muted: '#5F6675', accent: '#4F46E5', accent2: '#0EA5E9' },
    { id: 'nature', label: 'طبيعة', bg: '#F5F1E6', surface: '#E8E1CE', fg: '#1E2A1F', muted: '#5A6656', accent: '#2F7D4F', accent2: '#C9A227' },
    { id: 'sunset', label: 'غروب', bg: '#1F1024', surface: '#2D1734', fg: '#FFF4EC', muted: '#D8B8C6', accent: '#FF7A59', accent2: '#FFB547' },
    { id: 'ocean', label: 'محيط', bg: '#05233A', surface: '#0B3452', fg: '#EAF6FF', muted: '#9DC3D9', accent: '#3CC6D8', accent2: '#7BE0B8' },
    { id: 'mono', label: 'أحادي', bg: '#111111', surface: '#1D1D1D', fg: '#FAFAFA', muted: '#A3A3A3', accent: '#FAFAFA', accent2: '#737373' }
]

export const getSlideTheme = (id: SlideThemeId | string | undefined): SlideTheme =>
    SLIDE_THEMES.find(theme => theme.id === id) ?? SLIDE_THEMES[0]

export const THEME_OPTIONS: Array<Option<ThemePreference>> = [
    { id: 'system', label: 'تلقائي' },
    { id: 'light', label: 'فاتح' },
    { id: 'dark', label: 'داكن' }
]

export const ACCENTS: Array<Option<AccentId> & { swatch: string }> = [
    { id: 'saffron', label: 'زعفراني', swatch: '#E8962E' },
    { id: 'coral', label: 'مرجاني', swatch: '#E5574A' },
    { id: 'emerald', label: 'زمردي', swatch: '#1F9E73' },
    { id: 'sky', label: 'سماوي', swatch: '#2F7FD1' },
    { id: 'violet', label: 'بنفسجي', swatch: '#7C4DDB' }
]

export const FONT_SIZES: Array<Option<FontSizeId>> = [
    { id: 'sm', label: 'صغير' },
    { id: 'md', label: 'متوسط' },
    { id: 'lg', label: 'كبير' }
]

export const CHAT_MODELS: Array<Option<ChatPreferences['model']> & { description: string }> = [
    { id: 'fast', label: 'سريع', description: 'ردود فورية للاستخدام اليومي' },
    { id: 'smart', label: 'ذكي', description: 'تفكير أعمق وكتابة أقوى' }
]

export const CHAT_LENGTHS: Array<Option<ChatPreferences['length']>> = [
    { id: 'short', label: 'مختصر' },
    { id: 'balanced', label: 'متوازن' },
    { id: 'detailed', label: 'مفصل' }
]

export const CHAT_TONES: Array<Option<ChatPreferences['tone']>> = [
    { id: 'friendly', label: 'ودود' },
    { id: 'professional', label: 'رسمي' },
    { id: 'creative', label: 'إبداعي' }
]

export const LANGUAGES: Array<Option<ChatPreferences['language']>> = [
    { id: 'auto', label: 'نفس لغة رسالتي' },
    { id: 'ar', label: 'العربية' },
    { id: 'en', label: 'English' }
]

export const DEFAULT_SETTINGS: Settings = {
    appearance: {
        theme: 'system',
        accent: 'saffron',
        fontSize: 'md',
        reduceMotion: false
    },
    chat: {
        model: 'fast',
        length: 'balanced',
        tone: 'friendly',
        language: 'auto',
        instructions: '',
        enterToSend: true,
        autoTools: true,
        showSuggestions: true
    },
    image: {
        style: 'auto',
        aspectRatio: '1:1',
        count: 1,
        quality: 'high',
        enhance: true
    },
    video: {
        duration: 10,
        aspectRatio: '16:9',
        resolution: '720p',
        audio: true,
        style: 'cinematic',
        storyboard: true
    },
    slides: {
        count: 8,
        theme: 'royal',
        images: true,
        tone: 'professional',
        language: 'auto'
    }
}

export const SUGGESTIONS: Array<{ mode: StudioMode, text: string }> = [
    { mode: 'image', text: 'شعار أنيق لمقهى اسمه «بُنّ» بألوان دافئة' },
    { mode: 'video', text: 'غروب الشمس فوق نهر النيل والمراكب الشراعية تتحرك ببطء' },
    { mode: 'slides', text: 'عرض تقديمي عن مستقبل الطاقة الشمسية في الوطن العربي' },
    { mode: 'chat', text: 'اكتب لي خطة محتوى لمدة أسبوع لمتجر ملابس على إنستغرام' },
    { mode: 'image', text: 'صورة منتج احترافية لزجاجة عطر فاخرة على رخام أسود' },
    { mode: 'chat', text: 'اقترح عليّ أسماء مميزة لمشروع حلويات منزلية' }
]

export const formatSeconds = (seconds: number): string => {
    const safe = Math.max(0, Math.round(seconds))

    if (safe < 60) return `${safe} ث`

    const minutes = Math.floor(safe / 60)
    const rest = safe % 60

    return rest === 0 ? `${minutes} د` : `${minutes} د ${rest} ث`
}
